<template>
  <div class="customer-settings">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ name: 'customersIndex' }">
        {{ $t("menuItems.customersIndex") }}
      </el-breadcrumb-item>
      <el-breadcrumb-item>{{ customerName }}</el-breadcrumb-item>
      <el-breadcrumb-item>{{ $t("menuItems.customersSettings") }}</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card>
      <div slot="header">
        <span>{{ $t("customers.settings.filenameConfiguration") }}</span>
      </div>

      <el-form label-position="top">
        <el-form-item :label="$t('customers.settings.filenameCompositor')">
          <p class="compositor-instructions">
            {{ $t("customers.settings.compositorInstructions") }}
          </p>
          <FilenameCompositor
            ref="compositor"
            v-model="filenameTemplate"
            :tag-labels="tagLabels"
          />
        </el-form-item>

        <TagSelection
          :available-tags="availableTags"
          @tag-selected="handleTagSelected"
        />

        <el-form-item :label="$t('customers.settings.preview')">
          <div class="preview-box">
            <code>{{ previewFilename }}</code>
          </div>
        </el-form-item>

        <el-form-item>
          <el-button
            type="primary"
            :loading="isSaving"
            :disabled="!hasAtLeastOneTag"
            @click="saveSettings"
          >
            {{ $t("customers.settings.save") }}
          </el-button>
          <el-button @click="goBack">
            {{ $t("customers.settings.cancel") }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- Order Export Settings -->
    <el-card>
      <div slot="header">
        <span>{{ $t("customers.settings.orderExport.title") }}</span>
      </div>

      <el-form label-position="top">
        <el-form-item>
          <el-switch
            v-model="orderExportEnabled"
            :active-text="$t('customers.settings.orderExport.enableLabel')"
            @change="onExportEnabledChange"
          />
        </el-form-item>

        <template v-if="orderExportEnabled">
          <el-form-item :label="$t('customers.settings.orderExport.columnsTitle')">
            <el-row
              type="flex"
              justify="center"
            >
              <el-transfer
                v-model="selectedDataColumnKeys"
                :data="allDataColumnOptions"
                :titles="[
                  $t('customers.settings.orderExport.availableColumns'),
                  $t('customers.settings.orderExport.selectedColumns'),
                ]"
                filterable
                @change="handleTransferChange"
              />
            </el-row>
          </el-form-item>

          <el-form-item>
            <el-table
              :data="orderedColumns"
              border
              size="small"
              style="width: 100%"
            >
              <el-table-column
                type="index"
                label="#"
                width="50"
              />
              <el-table-column
                :label="$t('customers.settings.orderExport.emptyColumnLabel')"
                prop="displayLabel"
              />
              <el-table-column
                label="Type"
                width="100"
              >
                <template slot-scope="scope">
                  <el-tag
                    :type="scope.row.type === 'data' ? '' : 'warning'"
                    size="small"
                  >
                    {{
                      scope.row.type === "data"
                        ? $t("customers.settings.orderExport.columnType.data")
                        : $t("customers.settings.orderExport.columnType.empty")
                    }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('filters.actions')"
                width="160"
              >
                <template slot-scope="scope">
                  <el-tooltip
                    :value="shiftHintColumnId === columnId(scope.row)"
                    :content="$t('customers.settings.orderExport.shiftClickHint')"
                    placement="top"
                    manual
                  >
                    <el-button-group>
                      <el-button
                        size="mini"
                        icon="el-icon-top"
                        :disabled="scope.$index === 0"
                        @click="moveColumnUp(scope.$index, $event)"
                      />
                      <el-button
                        size="mini"
                        icon="el-icon-bottom"
                        :disabled="scope.$index === orderedColumns.length - 1"
                        @click="moveColumnDown(scope.$index, $event)"
                      />
                      <el-button
                        size="mini"
                        type="danger"
                        icon="el-icon-delete"
                        @click="removeColumn(scope.$index)"
                      />
                    </el-button-group>
                  </el-tooltip>
                </template>
              </el-table-column>
            </el-table>
          </el-form-item>

          <el-form-item>
            <el-button
              type="default"
              icon="el-icon-plus"
              @click="emptyColumnDialogVisible = true"
            >
              {{ $t("customers.settings.orderExport.addEmptyColumn") }}
            </el-button>
          </el-form-item>
        </template>

        <el-form-item>
          <el-button
            type="primary"
            :loading="isExportSaving"
            :disabled="!canSaveExportSettings"
            @click="saveOrderExportSettings"
          >
            {{ $t("customers.settings.orderExport.save") }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- Empty Column Dialog -->
    <el-dialog
      :visible="emptyColumnDialogVisible"
      width="25%"
      @close="emptyColumnDialogVisible = false"
    >
      <ContentHeader
        slot="title"
        :title="$t('customers.settings.orderExport.addEmptyColumn')"
      />
      <el-form
        label-position="top"
        @submit.native.prevent
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="24">
            <el-form-item :label="$t('customers.settings.orderExport.emptyColumnLabel')">
              <el-input
                v-model="newEmptyColumnLabel"
                :placeholder="$t('customers.settings.orderExport.emptyColumnLabel')"
                @keyup.enter.native="addEmptyColumn"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <FormButtons>
          <el-button @click="emptyColumnDialogVisible = false">
            {{ $t("customers.settings.cancel") }}
          </el-button>
          <el-button
            type="primary"
            :disabled="!newEmptyColumnLabel.trim()"
            @click="addEmptyColumn"
          >
            OK
          </el-button>
        </FormButtons>
      </el-form>
    </el-dialog>
  </div>
</template>

<script>
import { customersApi } from '@/api';
import ContentHeader from '@/components/ContentHeader.vue';
import FilenameCompositor from '@/components/FilenameCompositor.vue';
import FormButtons from '@/components/FormButtons.vue';
import TagSelection from '@/components/TagSelection.vue';

const TOOLTIP_MOVE_TRIGGER = 5;

export default {
  name: 'CustomerSettings',
  components: {
    ContentHeader,
    FilenameCompositor,
    FormButtons,
    TagSelection,
  },
  data() {
    return {
      customerName: this.$route.params.customerName || '',
      filenameTemplate: [],
      isSaving: false,
      isLoading: false,
      // Order export settings
      orderExportEnabled: false,
      selectedDataColumnKeys: [],
      orderedColumns: [],
      isExportSaving: false,
      emptyColumnDialogVisible: false,
      newEmptyColumnLabel: '',
      // Shift+click hint tracking
      lastMoveClick: {
        direction: null, columnId: null, timestamp: 0, count: 0,
      },
      shiftHintColumnId: null,
      shiftHintTimer: null,
    };
  },
  computed: {
    customerId() {
      return this.$route.params.customerId;
    },
    tagLabels() {
      return {
        labSampleCode: this.$t('customers.settings.tagLabels.labSampleCode'),
        productCode: this.$t('customers.settings.tagLabels.productCode'),
        productName: this.$t('customers.settings.tagLabels.productName'),
        customerProductName: this.$t('customers.settings.tagLabels.customerProductName'),
        lot: this.$t('customers.settings.tagLabels.lot'),
      };
    },
    availableTags() {
      const allTags = [
        { key: 'labSampleCode', label: this.tagLabels.labSampleCode },
        { key: 'productCode', label: this.tagLabels.productCode },
        { key: 'productName', label: this.tagLabels.productName },
        { key: 'customerProductName', label: this.tagLabels.customerProductName },
        { key: 'lot', label: this.tagLabels.lot },
      ];

      // Filter out tags that are already in the template
      const usedTagKeys = this.filenameTemplate
        .filter(item => item.type === 'tag')
        .map(item => item.value);

      return allTags.filter(tag => !usedTagKeys.includes(tag.key));
    },
    previewFilename() {
      if (!this.filenameTemplate || this.filenameTemplate.length === 0) {
        return 'example-filename.pdf';
      }

      const previewValues = {
        labSampleCode: '00000.00X00000',
        productCode: 'PROD001',
        productName: 'Prod. Name',
        customerProductName: 'CUST-SAMPLE-CODE-456',
        lot: 'LOT-123',
      };

      const filename = this.filenameTemplate
        .map((item) => {
          if (item.type === 'tag') {
            return previewValues[item.value] || item.value;
          }
          return item.value;
        })
        .join('');

      return `${filename}.pdf`;
    },
    hasAtLeastOneTag() {
      return this.filenameTemplate.some(item => item.type === 'tag');
    },
    allDataColumnKeys() {
      return [
        'sampleReportCode',
        'productCode',
        'productName',
        'customerProductName',
        'lot',
        'lotDate',
        'sector',
        'sampleDescription',
        'packagesNumber',
        'notes',
        'finalCustomer',
        'finalCustomerRef',
        'costCenter',
        'analysisCode',
        'analysisDescription',
        'offerDocNumber',
        'unitPrice',
        'orderId',
        'customerOrderNumber',
        'orderSentAt',
        'expectedDeliveryDate',
        'reportDate',
      ];
    },
    allDataColumnOptions() {
      return this.allDataColumnKeys.map(key => ({
        key,
        label: this.$t(`customers.settings.orderExport.columns.${key}`),
        disabled: false,
      }));
    },
    canSaveExportSettings() {
      if (!this.orderExportEnabled) return true;
      return this.orderedColumns.length > 0;
    },
  },
  created() {
    this.loadSettings();
    this.loadOrderExportSettings();
  },
  beforeDestroy() {
    clearTimeout(this.shiftHintTimer);
  },
  methods: {
    async loadSettings() {
      try {
        this.isLoading = true;
        const response = await customersApi.getFilenameSettings(this.customerId);

        if (response.data && response.data.filenameTemplate) {
          this.filenameTemplate = response.data.filenameTemplate;
        } else {
          this.filenameTemplate = [{ type: 'tag', value: 'labSampleCode' }];
        }
      } catch (error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message: error.response?.data?.error?.message || this.$t('customers.settings.saveError'),
        });
      } finally {
        this.isLoading = false;
      }
    },
    async saveSettings() {
      try {
        this.isSaving = true;
        await customersApi.saveFilenameSettings(this.customerId, {
          filenameTemplate: this.filenameTemplate,
        });
        this.$notify({
          showClose: true,
          type: 'success',
          message: this.$t('customers.settings.saveSuccess'),
        });
        setTimeout(() => {
          this.goBack();
        }, 1000);
      } catch (error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message: error.response?.data?.error?.message || this.$t('customers.settings.saveError'),
        });
      } finally {
        this.isSaving = false;
      }
    },
    handleTagSelected(tagKey) {
      if (this.$refs.compositor) {
        this.$refs.compositor.insertTag(tagKey);
      }
    },
    goBack() {
      this.$router.push({ name: 'customersIndex' });
    },
    selectAllDefaultColumns() {
      this.selectedDataColumnKeys = [...this.allDataColumnKeys];
      this.orderedColumns = this.allDataColumnKeys.map(key => ({
        type: 'data',
        key,
        displayLabel: this.$t(`customers.settings.orderExport.columns.${key}`),
      }));
    },
    onExportEnabledChange(enabled) {
      if (enabled && this.orderedColumns.length === 0) {
        this.selectAllDefaultColumns();
      }
    },
    async loadOrderExportSettings() {
      try {
        const response = await customersApi.getOrderExportSettings(this.customerId);
        const { enabled, columns } = response.data;
        this.orderExportEnabled = enabled || false;
        if (columns && columns.length > 0) {
          this.orderedColumns = columns.map(col => ({
            ...col,
            displayLabel:
              col.type === 'data'
                ? this.$t(`customers.settings.orderExport.columns.${col.key}`)
                : col.label,
          }));
          this.selectedDataColumnKeys = columns.filter(c => c.type === 'data').map(c => c.key);
        } else if (enabled) {
          this.selectAllDefaultColumns();
        }
      } catch (error) {
        // No settings yet - select all defaults
        this.selectAllDefaultColumns();
      }
    },
    handleTransferChange(_, direction, movedKeys) {
      if (direction === 'right') {
        // Add new data columns to the end of ordered list
        movedKeys.forEach((key) => {
          this.orderedColumns.push({
            type: 'data',
            key,
            displayLabel: this.$t(`customers.settings.orderExport.columns.${key}`),
          });
        });
      } else {
        // Remove deselected data columns from ordered list
        this.orderedColumns = this.orderedColumns.filter(
          col => !(col.type === 'data' && movedKeys.includes(col.key)),
        );
      }
    },
    columnId(col) {
      return col.type === 'data' ? `data:${col.key}` : `empty:${col.label}`;
    },
    trackMoveClick(direction, col) {
      const now = Date.now();
      const id = this.columnId(col);
      const last = this.lastMoveClick;
      if (last.direction === direction && last.columnId === id && now - last.timestamp < 1500) {
        last.count += 1;
      } else {
        last.count = 1;
      }
      last.direction = direction;
      last.columnId = id;
      last.timestamp = now;

      if (last.count >= TOOLTIP_MOVE_TRIGGER) {
        this.shiftHintColumnId = id;
        clearTimeout(this.shiftHintTimer);
        this.shiftHintTimer = setTimeout(() => { this.shiftHintColumnId = null; }, 3000);
        last.count = 0;
      }
    },
    moveColumnUp(index, event) {
      if (index <= 0) return;
      const col = this.orderedColumns[index];
      const cols = [...this.orderedColumns];
      if (event && event.shiftKey) {
        const [item] = cols.splice(index, 1);
        cols.unshift(item);
      } else {
        [cols[index - 1], cols[index]] = [cols[index], cols[index - 1]];
        this.trackMoveClick('up', col);
      }
      this.orderedColumns = cols;
    },
    moveColumnDown(index, event) {
      if (index >= this.orderedColumns.length - 1) return;
      const col = this.orderedColumns[index];
      const cols = [...this.orderedColumns];
      if (event && event.shiftKey) {
        const [item] = cols.splice(index, 1);
        cols.push(item);
      } else {
        [cols[index], cols[index + 1]] = [cols[index + 1], cols[index]];
        this.trackMoveClick('down', col);
      }
      this.orderedColumns = cols;
    },
    removeColumn(index) {
      const col = this.orderedColumns[index];
      this.orderedColumns.splice(index, 1);
      // Also remove from transfer selection if it's a data column
      if (col.type === 'data') {
        this.selectedDataColumnKeys = this.selectedDataColumnKeys.filter(k => k !== col.key);
      }
    },
    addEmptyColumn() {
      const label = this.newEmptyColumnLabel.trim();
      if (!label) return;
      this.orderedColumns.push({
        type: 'empty',
        label,
        displayLabel: label,
      });
      this.newEmptyColumnLabel = '';
      this.emptyColumnDialogVisible = false;
    },
    async saveOrderExportSettings() {
      try {
        this.isExportSaving = true;
        // Build the columns array for the API (strip displayLabel)
        const columns = this.orderedColumns.map((col) => {
          if (col.type === 'data') {
            return { type: 'data', key: col.key };
          }
          return { type: 'empty', label: col.label };
        });
        await customersApi.saveOrderExportSettings(this.customerId, {
          enabled: this.orderExportEnabled,
          columns,
        });
        this.$notify({
          showClose: true,
          type: 'success',
          message: this.$t('customers.settings.orderExport.saveSuccess'),
        });
      } catch (error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message:
            error.response?.data?.error?.message
            || this.$t('customers.settings.orderExport.saveError'),
        });
      } finally {
        this.isExportSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.customer-settings {
  padding: 1.5rem;
}

.compositor-instructions {
  font-size: 0.875rem;
  color: #606266;
  margin-bottom: 0.5rem;
  line-height: 1.5;
}

.preview-box {
  background-color: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 0.25rem;
  padding: 0.5rem 1rem;
  font-family: monospace;
  color: #303133;
}

.el-card + .el-card {
  margin-top: 1.5rem;
}

.el-breadcrumb {
  margin-bottom: 1.5rem;
}
</style>

<style lang="scss">
// Global styles (not scoped) to fix breadcrumb separator on last item
.customer-settings .el-breadcrumb__item:last-child .el-breadcrumb__separator {
  display: none;
}
</style>
